import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from '../../contexts/UserContext';
import { getLscBillingStatus, getLscCommissionerAccess, LscApiError } from '../api';
import {
  resolveCommissionerAccessPresentation,
  type CommissionerAccessPresentation,
} from '../commissionerAccess';
import type { LscBillingStatusResponse, LscCommissionerAccessResponse } from '../types';

export type UseCommissionerAccessResult = {
  loading: boolean;
  error: string | null;
  access: LscCommissionerAccessResponse | null;
  billingStatus: LscBillingStatusResponse | null;
  presentation: CommissionerAccessPresentation;
  reload: () => Promise<void>;
};

export function useCommissionerAccess(enabled = true): UseCommissionerAccessResult {
  const { user, loading: authLoading } = useUser();
  const [access, setAccess] = useState<LscCommissionerAccessResponse | null>(null);
  const [billingStatus, setBillingStatus] = useState<LscBillingStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || authLoading) {
      return;
    }
    if (!user) {
      setAccess(null);
      setBillingStatus(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [accessResult, billingResult] = await Promise.all([
        getLscCommissionerAccess(),
        getLscBillingStatus().catch(() => null),
      ]);
      setAccess(accessResult);
      setBillingStatus(billingResult);
    } catch (err) {
      setAccess(null);
      setBillingStatus(null);
      if (err instanceof LscApiError) {
        setError(err.message || 'Could not load commissioner access.');
      } else {
        setError('Could not load commissioner access.');
      }
    } finally {
      setLoading(false);
    }
  }, [authLoading, enabled, user]);

  useEffect(() => {
    void load();
  }, [load]);

  const presentation = useMemo(
    () =>
      resolveCommissionerAccessPresentation(access, {
        isAuthenticated: Boolean(user),
        billingStatus,
      }),
    [access, billingStatus, user]
  );

  return {
    loading: authLoading || loading,
    error,
    access,
    billingStatus,
    presentation,
    reload: load,
  };
}
